import { useConfirm } from "primevue";
import { useActionFailedAlert } from "shared/problems/composables/actionFailedAlert";
import { useDeleteStorageArea } from "../api/deleteStorageArea";
import { useLanguageStore } from "shared/translations/stores/languageStore";
import { storeToRefs } from "pinia";
import { useUndoDeleteStorageArea, type UndoDeleteStorageAreaRequest } from "../api/undoDeleteStorageArea";
import { useUndoStore } from "shared/undo/stores/undoStore";
import { useStorageAreaStore } from "shared/storageAreas/stores/storageAreaStore";
import { Assert } from "shared/assert";

export function useDeleteStorageAreaConfirm() {
  const confirm = useConfirm();
  const actionFailedAlert = useActionFailedAlert();
  const deleteStorageArea = useDeleteStorageArea();
  const undoDeleteStorageArea = useUndoDeleteStorageArea();
  const undoStore = useUndoStore();
  const storageAreaStore = useStorageAreaStore();

  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  return {
    show
  };

  async function show(options: ShowConfirm) {
    const storageAreaId = options.storageAreaId;

    const storageArea = storageAreaStore.getById(storageAreaId);

    Assert.definied(storageArea);

    const promise = new Promise<void>((resolve, reject) => {

      confirm.require({
        message: translations.value.dialogs.storageArea.confirmDeleteMessage(storageArea.name),
        header: translations.value.dialogs.storageArea.confirmDeleteHeader,
        icon: "pi pi-info-circle",
        rejectProps: {
          label: translations.value.generic.abort,
          severity: "secondary",
          outlined: true,
        },
        acceptProps: {
          label: translations.value.generic.delete,
          severity: "danger",
        },
        accept: async () => {
          try {
            const response = await deleteStorageArea.execute({ storageAreaId });

            if (!response.ok) {
              actionFailedAlert.show(response.details);
              reject();
              return;
            }

            const undoRequest: UndoDeleteStorageAreaRequest = {
              storageAreaId: storageArea.id
            };

            undoStore.add({
              title: translations.value.undo.deleteStorageAreaTitle,
              description: translations.value.undo.getUndoDeleteStorageAreaDescription(storageArea.name),
              function: async () => {
                const response = await undoDeleteStorageArea.execute(undoRequest);
                if (!response.ok) {
                  actionFailedAlert.show(response.details);
                  return;
                }
              }
            });

            resolve();

          } catch {
            reject();
          }
        },
        reject: () => {
          reject();
        }
      });
    });

    return promise;
  }
}

interface ShowConfirm {
  storageAreaId: string;
}
